import type { SpriteKind } from '../content/units';
import { drawBoxModel } from './boxes';
import type { BoxModel } from './boxes';
import {
    AA_MODEL,
    AIRFIELD_MODEL,
    AT_INFANTRY,
    BARRACKS_MODEL,
    FACTORY_MODEL,
    GUNSHIP,
    HARVESTER,
    HOUND,
    INFANTRY,
    PILLBOX_MODEL,
    POWER_MODEL,
    REFINERY_MODEL,
    TANK,
    TILE_UNITS,
    TURRET_MODEL,
    YARD_MODEL,
} from './models';

/*
 * Atlas ספרייטים פרוצדורלי.
 *
 * כל יחידה מצוירת כווקטור לתוך קנבס אחד בעליית המשחק — לכל צבע שחקן ולכל זווית
 * סיבוב. משם והלאה הציור הוא `drawImage` בלבד, שהיא הפעולה שמחזיקה מאות יחידות
 * ב-60fps גם בטלפון.
 *
 * למה פרוצדורלי ולא נכסים: אפס קבצים להוריד (המשחק חייב לעבוד אופליין), אפס בעיות
 * רישוי, צבעי קבוצה בחינם, ויחידה חדשה היא פונקציית ציור — לא חיפוש ספרייט תואם.
 *
 * שלושה דברים שמפרידים בין "מלבן עם עיגול" לבין יחידה שנראית כמו יחידה:
 *
 *   1. **תא גדול מספיק.** התא חייב להיות לפחות בגודל המרבי שבו היחידה תצויר, אחרת
 *      בזום המרבי מגדילים תמונה קטנה והיא מטושטשת. בזום 64 טנק מצויר ב-88px, ולכן
 *      התא הוא 128.
 *   2. **תאורה שקבועה בעולם ולא ביחידה.** אם המדרג מצויר בקואורדינטות המקומיות, הוא
 *      מסתובב יחד עם היחידה והתאורה "רודפת" אחריה. כאן כיוון האור מחושב לכל זווית
 *      בנפרד (`LIGHT_ANGLE - spriteAngle`), כך שכל היחידות מוארות מאותו כיוון בשדה.
 *   3. **מתאר כהה.** בלעדיו יחידה כחולה על דשא כהה נבלעת ברקע.
 *
 * הציור חי כאן ולא ב-content/ כדי שתיקיית התוכן תישאר דאטה טהורה בלי תלות בקנבס;
 * ההגדרה שם נושאת רק `sprite`, שהוא רמז ולא לוגיקה.
 */

/**
 * גודל תא ב-atlas, בפיקסלים.
 * חייב להיות ≥ הגודל המרבי שבו יחידה מצוירת: 3.2 × (110/256) × MAX_ZOOM ≈ 88.
 */
const CELL = 128;

/** מספר זוויות. 16 מספיקות כדי שסיבוב ייראה רציף ואינן מנפחות את ה-atlas. */
export const ROTATIONS = 16;

/*
 * הסדר כאן הוא **מיקום בתוך ה-atlas בלבד** ולא מזהה שנשמר, ולכן הוא בטוח לשינוי —
 * בניגוד לסדר ב-`content/units`. ה-atlas גדל בשורה לכל ספרייט × צבע שחקן, ולכן
 * ספרייט חדש עולה 128px של גובה לכל צבע ולא יותר.
 */
const SPRITE_ORDER: readonly SpriteKind[] = [
    'tank',
    'infantry',
    'harvester',
    'gunship',
    'atInfantry',
    'hound',
];

/** מיפוי מרמז הציור שבתוכן למודל. `sprite` הוא רמז ולא לוגיקה — ראו content/units. */
export function unitModel(sprite: SpriteKind): BoxModel {
    return modelFor(sprite);
}

function modelFor(sprite: SpriteKind): BoxModel {
    if (sprite === 'tank') return TANK;
    if (sprite === 'harvester') return HARVESTER;
    if (sprite === 'gunship') return GUNSHIP;
    if (sprite === 'atInfantry') return AT_INFANTRY;
    if (sprite === 'hound') return HOUND;
    return INFANTRY;
}

export interface SpriteAtlas {
    readonly canvas: HTMLCanvasElement;
    readonly cell: number;
    readonly rotations: number;
    /** אינדקס הצבע בתוך ה-atlas, לפי סדר הצבעים שהועברו בבנייה. */
    colorIndex(playerColor: string): number;
    sourceX(rotation: number): number;
    sourceY(sprite: SpriteKind, colorIndex: number): number;
}

/**
 * בונה atlas עבור צבעי השחקנים שבמשחק הזה בלבד.
 * שמונה צבעים היו מנפחים אותו פי ארבעה בלי סיבה — במשחק של שניים יש שני צבעים.
 */
export function buildAtlas(playerColors: readonly string[]): SpriteAtlas {
    const canvas = document.createElement('canvas');
    canvas.width = CELL * ROTATIONS;
    canvas.height = CELL * SPRITE_ORDER.length * playerColors.length;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('לא ניתן ליצור הקשר קנבס ל-atlas');

    SPRITE_ORDER.forEach((sprite, spriteIndex) => {
        playerColors.forEach((color, colorIndex) => {
            const row = spriteIndex * playerColors.length + colorIndex;
            for (let rotation = 0; rotation < ROTATIONS; rotation++) {
                const angle = (rotation / ROTATIONS) * Math.PI * 2;
                ctx.save();
                ctx.translate(rotation * CELL + CELL / 2, row * CELL + CELL / 2);
                ctx.lineJoin = 'round';
                ctx.lineCap = 'round';
                /*
                 * הסיבוב נעשה **בתוך המנוע**, על הקודקודים, ולא על הקנבס. זה מה
                 * שמאפשר לתאורה להישאר קבועה בעולם: אילו הקנבס היה מסובב, כל
                 * הצללה הייתה מסתובבת יחד עם היחידה.
                 */
                drawBoxModel(ctx, modelFor(sprite), angle, color);
                ctx.restore();
            }
        });
    });

    const colorLookup = new Map(playerColors.map((color, index) => [color, index]));

    return {
        canvas,
        cell: CELL,
        rotations: ROTATIONS,
        colorIndex: (playerColor) => colorLookup.get(playerColor) ?? 0,
        sourceX: (rotation) => ((((rotation % ROTATIONS) + ROTATIONS) % ROTATIONS) * CELL),
        sourceY: (sprite, colorIndex) => {
            const spriteIndex = Math.max(0, SPRITE_ORDER.indexOf(sprite));
            return (spriteIndex * playerColors.length + colorIndex) * CELL;
        },
    };
}

/** מבהיר או מכהה צבע hex. amount חיובי מבהיר, שלילי מכהה. */
export function shade(hex: string, amount: number): string {
    const value = parseInt(hex.slice(1), 16);
    const channel = (shift: number) => {
        const base = (value >> shift) & 0xff;
        const target = amount >= 0 ? 255 : 0;
        return Math.round(base + (target - base) * Math.abs(amount));
    };
    return `rgb(${channel(16)},${channel(8)},${channel(0)})`;
}

/*
 * מבנים.
 *
 * **אותו מנוע קופסאות בדיוק כמו היחידות**, רק בלי סיבוב: מבנה אינו פונה לשום
 * כיוון. ההצללה, מיון הפאות והמתאר מגיעים בחינם, וזו הסיבה שהשלב הזה עלה הרבה
 * פחות מהראשון — המנוע כבר היה כתוב.
 *
 * המודלים מתוארים בקנה מידה של `TILE_UNITS` ליחידת אריח, ומוקטנים כאן לגודל
 * שהמבנה תופס על המסך. כך מבנה חדש מתואר במידות שמתאימות ל-`w`/`h` שלו בתוכן.
 */
export interface BuildingSprites {
    /**
     * מצייר מבנה שמרכזו ב-(cx, cy), כשאריח אחד שווה `pixelsPerTile` פיקסלים.
     */
    draw(
        ctx: CanvasRenderingContext2D,
        sprite: string,
        cx: number,
        cy: number,
        pixelsPerTile: number,
        color: string,
    ): void;
}

function buildingModel(sprite: string): BoxModel {
    if (sprite === 'yard') return YARD_MODEL;
    if (sprite === 'refinery') return REFINERY_MODEL;
    if (sprite === 'power') return POWER_MODEL;
    if (sprite === 'barracks') return BARRACKS_MODEL;
    if (sprite === 'factory') return FACTORY_MODEL;
    if (sprite === 'turret') return TURRET_MODEL;
    if (sprite === 'airfield') return AIRFIELD_MODEL;
    if (sprite === 'aaGun') return AA_MODEL;
    return PILLBOX_MODEL;
}

export const buildingSprites: BuildingSprites = {
    draw(ctx, sprite, cx, cy, pixelsPerTile, color) {
        const scale = pixelsPerTile / TILE_UNITS;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(scale, scale);
        ctx.lineJoin = 'round';
        drawBoxModel(ctx, buildingModel(sprite), 0, color);
        ctx.restore();
    },
};
